import { useEffect, useMemo, useRef, useState } from 'react';
import { localAnchorPoint, nodeBounds, routeEdge } from '../core/geometry';
import type { AnchorSide, ConnectorEndpoint, DiagramEdge, DiagramNode, DiagramPage, Point, ViewportState } from '../core/types';
import type { EditorPreferences } from '../services/persistence/preferences';
import { computeRenderSubset } from '../core/renderCulling';
import { buildSpatialEdgeIndex, buildSpatialNodeIndex } from '../core/spatialIndex';

interface Props {
  page: DiagramPage;
  selectedNodeIds: string[];
  selectedEdgeIds: string[];
  viewport: ViewportState;
  connectionMode: boolean;
  connectionSourceId: string | null;
  connectionSourceAnchor: AnchorSide;
  preferences: EditorPreferences;
  readOnly?: boolean;
  onSelectNode: (id: string | null, additive?: boolean) => void;
  onSelectEdge: (id: string | null, additive?: boolean) => void;
  onSelectArea: (ids: string[], additive?: boolean) => void;
  onMoveNodes: (changes: Array<{ id: string; x: number; y: number }>, commit?: boolean) => void;
  onResizeNode: (id: string, width: number, height: number, commit?: boolean) => void;
  onTransformNodes: (changes: Array<{ id: string; x: number; y: number; width: number; height: number }>, commit?: boolean) => void;
  onRotateNode: (id: string, rotation: number, commit?: boolean) => void;
  onViewport: (viewport: ViewportState) => void;
  onNodeForConnection: (id: string, anchor?: AnchorSide, keepSource?: boolean) => void;
  onEditNodeText: (id: string, text: string) => void;
  onEditEdgeLabel: (id: string, label: string) => void;
  onUpdateEdgeWaypoints: (id: string, waypoints: Point[], commit?: boolean) => void;
  onDropShape: (type: string, x: number, y: number) => void;
  onNavigateNodeLink: (pageId: string) => void;
  onContextMenuRequest: (request: { x: number; y: number; target: 'canvas' | 'node' | 'edge' }) => void;
  remoteUsers?: Array<{ userId: string; email: string; pageId: string | null; nodeIds: string[]; cursor: Point | null; permission?: string }>;
  onCursor?: (point: Point | null) => void;
  commentCounts?: Record<string, number>;
  renderThreshold?: number;
  cullingMarginPx?: number;
  adaptiveQuality?: boolean;
  onOpenNodeComments?: (nodeId: string) => void;
}

interface DragState {
  pointerId: number;
  startClientX: number;
  startClientY: number;
  starts: Array<{ id: string; x: number; y: number }>;
}

interface ResizeState {
  id: string;
  pointerId: number;
  startClientX: number;
  startClientY: number;
  startWidth: number;
  startHeight: number;
}

interface RotateState {
  id: string;
  pointerId: number;
  offset: number;
}

interface GroupResizeState {
  pointerId: number;
  handle: 'nw' | 'ne' | 'sw' | 'se';
  startClientX: number;
  startClientY: number;
  bounds: { left: number; top: number; right: number; bottom: number };
  starts: Array<{ id: string; x: number; y: number; width: number; height: number }>;
}

interface WaypointDragState {
  edgeId: string;
  index: number;
  pointerId: number;
  working: Point[];
}

interface EqualSpacingGuide {
  axis: 'horizontal' | 'vertical';
  gap: number;
  firstStart: number;
  firstEnd: number;
  secondStart: number;
  secondEnd: number;
  cross: number;
}

interface DistanceGuide {
  axis: 'horizontal' | 'vertical';
  from: number;
  to: number;
  cross: number;
  gap: number;
}

interface PanState {
  pointerId: number;
  startClientX: number;
  startClientY: number;
  startPanX: number;
  startPanY: number;
}

interface MarqueeState {
  pointerId: number;
  start: Point;
  end: Point;
  additive: boolean;
}

interface EditState {
  id: string;
  text: string;
}

interface EdgeEditState {
  id: string;
  label: string;
}

const ANCHORS: Array<Exclude<AnchorSide, 'auto'>> = ['topLeft','top','topRight','rightTop','right','rightBottom','bottomRight','bottom','bottomLeft','leftBottom','left','leftTop'];

export function NodeShape({ node }: { node: DiagramNode }) {
  const common = { fill: node.style.fill, stroke: node.style.stroke, strokeWidth: node.style.strokeWidth, opacity: node.style.opacity, strokeDasharray: node.style.linePattern === 'dashed' ? '7 5' : undefined };
  if (node.type === 'ellipse' || node.type === 'connector') return <ellipse cx={node.width / 2} cy={node.height / 2} rx={node.width / 2} ry={node.height / 2} {...common} />;
  if (node.type === 'diamond') return <polygon points={`${node.width / 2},0 ${node.width},${node.height / 2} ${node.width / 2},${node.height} 0,${node.height / 2}`} {...common} />;
  if (node.type === 'inputOutput') {
    const offset = Math.min(24, node.width * 0.14);
    return <polygon points={`${offset},0 ${node.width},0 ${node.width - offset},${node.height} 0,${node.height}`} {...common} />;
  }
  if (node.type === 'subprocess') {
    const inset = Math.min(16, node.width * 0.1);
    return <g><rect width={node.width} height={node.height} rx={node.style.radius} {...common} /><line x1={inset} x2={inset} y1={0} y2={node.height} stroke={node.style.stroke} strokeWidth={node.style.strokeWidth} strokeDasharray={node.style.linePattern === 'dashed' ? '7 5' : undefined} opacity={node.style.opacity} /><line x1={node.width - inset} x2={node.width - inset} y1={0} y2={node.height} stroke={node.style.stroke} strokeWidth={node.style.strokeWidth} strokeDasharray={node.style.linePattern === 'dashed' ? '7 5' : undefined} opacity={node.style.opacity} /></g>;
  }
  if (node.type === 'preparation') {
    const cut = Math.min(24, node.width * 0.14);
    return <polygon points={`${cut},0 ${node.width - cut},0 ${node.width},${node.height / 2} ${node.width - cut},${node.height} ${cut},${node.height} 0,${node.height / 2}`} {...common} />;
  }
  if (node.type === 'manualInput') {
    const cut = Math.min(24, node.height * 0.3);
    return <polygon points={`${cut},0 ${node.width},${cut} ${node.width},${node.height} 0,${node.height}`} {...common} />;
  }
  if (node.type === 'delay') {
    const radius = node.height / 2;
    return <path d={`M 0 0 H ${node.width - radius} A ${radius} ${radius} 0 0 1 ${node.width - radius} ${node.height} H 0 Z`} {...common} />;
  }
  if (node.type === 'note') {
    const fold = Math.min(22, node.width * 0.14, node.height * 0.3);
    return <g><path d={`M 0 0 H ${node.width - fold} L ${node.width} ${fold} V ${node.height} H 0 Z`} {...common} /><path d={`M ${node.width - fold} 0 V ${fold} H ${node.width}`} fill="none" stroke={node.style.stroke} strokeWidth={node.style.strokeWidth} strokeDasharray={node.style.linePattern === 'dashed' ? '7 5' : undefined} opacity={node.style.opacity} /></g>;
  }
  if (node.type === 'document') {
    const wave = Math.min(14, node.height * 0.16);
    return <path d={`M 0 0 H ${node.width} V ${node.height - wave} C ${node.width * .72} ${node.height + wave * .2}, ${node.width * .35} ${node.height - wave * 1.25}, 0 ${node.height} Z`} {...common} />;
  }
  if (node.type === 'database') {
    const cap = Math.min(18, node.height * 0.2);
    return (
      <g>
        <path d={`M 0 ${cap} C 0 0, ${node.width} 0, ${node.width} ${cap} V ${node.height - cap} C ${node.width} ${node.height}, 0 ${node.height}, 0 ${node.height - cap} Z`} {...common} />
        <ellipse cx={node.width / 2} cy={cap} rx={node.width / 2} ry={cap} {...common} />
        <path d={`M 0 ${node.height - cap} C 0 ${node.height}, ${node.width} ${node.height}, ${node.width} ${node.height - cap}`} fill="none" stroke={node.style.stroke} strokeWidth={node.style.strokeWidth} strokeDasharray={node.style.linePattern === 'dashed' ? '7 5' : undefined} opacity={node.style.opacity} />
      </g>
    );
  }
  if (node.type === 'bpmnSubprocess') {
    return <g><rect width={node.width} height={node.height} rx={Math.max(10,node.style.radius)} {...common}/><rect x={6} y={6} width={node.width-12} height={node.height-12} rx={Math.max(7,node.style.radius-2)} fill="none" stroke={node.style.stroke} strokeWidth={Math.max(1,node.style.strokeWidth*.7)} opacity={node.style.opacity}/><rect x={node.width/2-9} y={node.height-22} width={18} height={14} rx={2} fill={node.style.fill} stroke={node.style.stroke} strokeWidth={1} opacity={node.style.opacity}/><path d={`M ${node.width/2} ${node.height-19} V ${node.height-11} M ${node.width/2-4} ${node.height-15} H ${node.width/2+4}`} stroke={node.style.stroke} strokeWidth={1.3} opacity={node.style.opacity}/></g>;
  }
  if (node.type === 'bpmnDataObject') {
    const fold=Math.min(22,node.width*.24); return <g><path d={`M 0 0 H ${node.width-fold} L ${node.width} ${fold} V ${node.height} H 0 Z`} {...common}/><path d={`M ${node.width-fold} 0 V ${fold} H ${node.width}`} fill="none" stroke={node.style.stroke} strokeWidth={node.style.strokeWidth} opacity={node.style.opacity}/></g>;
  }
  if (node.type === 'bpmnTask') {
    const taskType = String(node.data.taskType ?? 'user');
    const marker = taskType === 'service' ? <circle cx={16} cy={16} r={6} fill="none" stroke={node.style.stroke} strokeWidth={1.3} opacity={node.style.opacity} />
      : taskType === 'manual' ? <path d="M 9 20 L 15 10 L 22 14 L 18 23 Z" fill="none" stroke={node.style.stroke} strokeWidth={1.3} opacity={node.style.opacity} />
      : taskType === 'script' ? <text x={9} y={20} fontSize={13} fontWeight={700} fill={node.style.stroke} opacity={node.style.opacity}>S</text>
      : taskType === 'businessRule' ? <text x={8} y={20} fontSize={12} fontWeight={700} fill={node.style.stroke} opacity={node.style.opacity}>R</text>
      : taskType === 'send' ? <text x={8} y={20} fontSize={13} fontWeight={700} fill={node.style.stroke} opacity={node.style.opacity}>→</text>
      : taskType === 'receive' ? <text x={8} y={20} fontSize={13} fontWeight={700} fill={node.style.stroke} opacity={node.style.opacity}>←</text>
      : <rect x={10} y={10} width={13} height={11} rx={2} fill="none" stroke={node.style.stroke} strokeWidth={1.2} opacity={node.style.opacity} />;
    return <g><rect width={node.width} height={node.height} rx={Math.max(10, node.style.radius)} {...common} />{marker}</g>;
  }
  if (node.type === 'bpmnEvent') {
    const eventType = String(node.data.eventType ?? 'start');
    const definition = String(node.data.eventDefinition ?? 'none');
    const strokeWidth = eventType === 'end' ? Math.max(3, node.style.strokeWidth * 2.2) : node.style.strokeWidth;
    const mark = definition === 'message' ? '✉' : definition === 'timer' ? '◷' : definition === 'error' ? '⚡' : definition === 'signal' ? '△' : definition === 'conditional' ? '≡' : definition === 'link' ? '↗' : definition === 'terminate' ? '●' : '';
    return <g><ellipse cx={node.width / 2} cy={node.height / 2} rx={node.width / 2 - 3} ry={node.height / 2 - 3} fill={node.style.fill} stroke={node.style.stroke} strokeWidth={strokeWidth} opacity={node.style.opacity} />{eventType === 'intermediate' && <ellipse cx={node.width / 2} cy={node.height / 2} rx={Math.max(4, node.width / 2 - 10)} ry={Math.max(4, node.height / 2 - 10)} fill="none" stroke={node.style.stroke} strokeWidth={Math.max(1, node.style.strokeWidth * .8)} opacity={node.style.opacity} />}{mark && <text x={node.width / 2} y={node.height / 2 + 6} textAnchor="middle" fontSize={18} fontWeight={700} fill={node.style.stroke} opacity={node.style.opacity}>{mark}</text>}</g>;
  }
  if (node.type === 'bpmnBoundaryEvent') {
    const definition = String(node.data.eventDefinition ?? 'timer');
    const mark = definition === 'message' ? '✉' : definition === 'timer' ? '◷' : definition === 'error' ? '⚡' : definition === 'signal' ? '△' : definition === 'conditional' ? '≡' : definition === 'cancel' ? '×' : definition === 'compensation' ? '«' : '';
    const interrupting = node.data.cancelActivity !== false;
    return <g><ellipse cx={node.width / 2} cy={node.height / 2} rx={node.width / 2 - 3} ry={node.height / 2 - 3} fill={node.style.fill} stroke={node.style.stroke} strokeWidth={Math.max(1.4, node.style.strokeWidth)} strokeDasharray={interrupting ? undefined : '5 4'} opacity={node.style.opacity} /><ellipse cx={node.width / 2} cy={node.height / 2} rx={Math.max(4, node.width / 2 - 9)} ry={Math.max(4, node.height / 2 - 9)} fill="none" stroke={node.style.stroke} strokeWidth={Math.max(1, node.style.strokeWidth * .75)} strokeDasharray={interrupting ? undefined : '4 3'} opacity={node.style.opacity} />{mark && <text x={node.width / 2} y={node.height / 2 + 6} textAnchor="middle" fontSize={16} fontWeight={700} fill={node.style.stroke} opacity={node.style.opacity}>{mark}</text>}</g>;
  }
  if (node.type === 'bpmnTextAnnotation') {
    const arm = Math.min(18, node.width * .12);
    return <g><path d={`M ${arm} 0 H 0 V ${node.height} H ${arm}`} fill="none" stroke={node.style.stroke} strokeWidth={Math.max(1.5, node.style.strokeWidth)} opacity={node.style.opacity} /></g>;
  }
  if (node.type === 'bpmnGateway') {
    const gatewayType = String(node.data.gatewayType ?? 'exclusive');
    const marker = gatewayType === 'parallel'
      ? <path d={`M ${node.width * .5} ${node.height * .28} V ${node.height * .72} M ${node.width * .28} ${node.height * .5} H ${node.width * .72}`} fill="none" stroke={node.style.stroke} strokeWidth={Math.max(2, node.style.strokeWidth * 1.35)} opacity={node.style.opacity} />
      : gatewayType === 'eventBased'
        ? <g><ellipse cx={node.width / 2} cy={node.height / 2} rx={node.width * .2} ry={node.height * .2} fill="none" stroke={node.style.stroke} strokeWidth={Math.max(1.6, node.style.strokeWidth)} opacity={node.style.opacity} /><circle cx={node.width / 2} cy={node.height / 2} r={4} fill={node.style.stroke} opacity={node.style.opacity} /></g>
        : gatewayType === 'complex'
          ? <text x={node.width / 2} y={node.height / 2 + 8} textAnchor="middle" fontSize={26} fontWeight={700} fill={node.style.stroke} opacity={node.style.opacity}>*</text>
        : gatewayType === 'inclusive'
        ? <ellipse cx={node.width / 2} cy={node.height / 2} rx={node.width * .18} ry={node.height * .18} fill="none" stroke={node.style.stroke} strokeWidth={Math.max(1.8, node.style.strokeWidth * 1.2)} opacity={node.style.opacity} />
        : <path d={`M ${node.width * .35} ${node.height * .35} L ${node.width * .65} ${node.height * .65} M ${node.width * .65} ${node.height * .35} L ${node.width * .35} ${node.height * .65}`} fill="none" stroke={node.style.stroke} strokeWidth={Math.max(1.5, node.style.strokeWidth * 1.1)} opacity={node.style.opacity} />;
    return <g><polygon points={`${node.width / 2},0 ${node.width},${node.height / 2} ${node.width / 2},${node.height} 0,${node.height / 2}`} {...common} />{marker}</g>;
  }
  if (node.type === 'bpmnPool' || node.type === 'bpmnLane') { const header = Math.min(36, node.width * .1); return <g><rect width={node.width} height={node.height} rx={node.type === 'bpmnPool' ? 6 : 2} {...common} /><line x1={header} x2={header} y1={0} y2={node.height} stroke={node.style.stroke} strokeWidth={node.style.strokeWidth} opacity={node.style.opacity} /></g>; }
  if (node.type === 'orgPerson') { const band = Math.min(30, node.height * .32); return <g><rect width={node.width} height={node.height} rx={Math.max(8, node.style.radius)} {...common} /><line x1={0} x2={node.width} y1={band} y2={band} stroke={node.style.stroke} strokeWidth={Math.max(1, node.style.strokeWidth * .7)} opacity={node.style.opacity} /></g>; }
  if (node.type === 'orgDepartment') { const band = Math.min(26, node.height * .28); return <g><rect width={node.width} height={node.height} rx={Math.max(6, node.style.radius)} {...common} /><rect x={0} y={0} width={node.width} height={band} rx={Math.max(6, node.style.radius)} fill={node.style.stroke} opacity={Math.min(.12, node.style.opacity)} pointerEvents="none" /></g>; }
  if (node.type === 'mindRoot') return <rect width={node.width} height={node.height} rx={Math.max(node.height / 2, node.style.radius)} {...common} />;
  if (node.type === 'mindTopic') return <g><rect width={node.width} height={node.height} rx={Math.max(10, node.style.radius)} {...common} /><line x1={14} x2={node.width - 14} y1={node.height - 8} y2={node.height - 8} stroke={node.style.stroke} strokeWidth={Math.max(2, node.style.strokeWidth)} opacity={node.style.opacity} /></g>;
  if (node.type === 'erdEntity') { const header = 34; return <g><rect width={node.width} height={node.height} rx={Math.max(6, node.style.radius)} {...common} /><rect x={0} y={0} width={node.width} height={header} rx={Math.max(6, node.style.radius)} fill={node.style.stroke} opacity={Math.min(.1, node.style.opacity)} pointerEvents="none" /><line x1={0} x2={node.width} y1={header} y2={header} stroke={node.style.stroke} strokeWidth={Math.max(1, node.style.strokeWidth)} opacity={node.style.opacity} /></g>; }
  if (node.type === 'umlClass') {
    const header = 34;
    const attributeHeight = Math.min(Math.max(38, (node.umlAttributes?.length ?? 0) * 18 + 12), Math.max(38, node.height - 76));
    const methodY = Math.min(node.height - 34, header + attributeHeight);
    return <g><rect width={node.width} height={node.height} rx={Math.max(2, Math.min(8, node.style.radius))} {...common} /><line x1={0} x2={node.width} y1={header} y2={header} stroke={node.style.stroke} strokeWidth={node.style.strokeWidth} opacity={node.style.opacity} /><line x1={0} x2={node.width} y1={methodY} y2={methodY} stroke={node.style.stroke} strokeWidth={node.style.strokeWidth} opacity={node.style.opacity} /></g>;
  }
  if (node.type === 'umlActor') {
    const cx = node.width / 2; const headY = 24; const headR = 12; const bodyTop = headY + headR; const bodyBottom = 78;
    return <g fill="none" stroke={node.style.stroke} strokeWidth={Math.max(1.5, node.style.strokeWidth)} opacity={node.style.opacity}><circle cx={cx} cy={headY} r={headR} fill={node.style.fill} /><line x1={cx} x2={cx} y1={bodyTop} y2={bodyBottom} /><line x1={cx - 26} x2={cx + 26} y1={52} y2={52} /><line x1={cx} x2={cx - 24} y1={bodyBottom} y2={108} /><line x1={cx} x2={cx + 24} y1={bodyBottom} y2={108} /></g>;
  }
  if (node.type === 'umlUseCase') return <ellipse cx={node.width / 2} cy={node.height / 2} rx={node.width / 2} ry={node.height / 2} {...common} />;
  if (node.type === 'umlPackage') { const tabW = Math.min(90, node.width * .35); const tabH = 24; return <path d={`M 0 ${tabH} V ${node.height} H ${node.width} V ${tabH} H ${tabW} V 0 H 0 Z`} {...common} />; }
  if (node.type === 'networkServer') {
    const rackY = [24, 46, 68];
    return <g><rect width={node.width} height={node.height} rx={Math.max(8, node.style.radius)} {...common} />{rackY.map((y) => <g key={y}><rect x={14} y={y} width={node.width - 28} height={16} rx={3} fill="none" stroke={node.style.stroke} strokeWidth={Math.max(1, node.style.strokeWidth * .75)} opacity={node.style.opacity} /><circle cx={node.width - 25} cy={y + 8} r={2.5} fill={node.style.stroke} opacity={node.style.opacity} /></g>)}</g>;
  }
  if (node.type === 'networkSwitch') {
    const ports = Array.from({ length: 8 }, (_, index) => index);
    return <g><rect width={node.width} height={node.height} rx={Math.max(7, node.style.radius)} {...common} /><rect x={12} y={24} width={node.width - 24} height={42} rx={5} fill="none" stroke={node.style.stroke} strokeWidth={Math.max(1, node.style.strokeWidth * .8)} opacity={node.style.opacity} />{ports.map((index) => <rect key={index} x={22 + index * Math.max(14, (node.width - 58) / 8)} y={38} width={9} height={11} rx={1.5} fill="none" stroke={node.style.stroke} strokeWidth={1} opacity={node.style.opacity} />)}</g>;
  }
  if (node.type === 'networkRouter') {
    const cx = node.width / 2; const cy = node.height / 2;
    return <g><ellipse cx={cx} cy={cy} rx={node.width * .42} ry={node.height * .36} {...common} /><path d={`M ${cx - 26} ${cy} H ${cx + 26} M ${cx + 17} ${cy - 8} L ${cx + 28} ${cy} L ${cx + 17} ${cy + 8} M ${cx} ${cy - 22} V ${cy + 22} M ${cx - 8} ${cy - 13} L ${cx} ${cy - 24} L ${cx + 8} ${cy - 13}`} fill="none" stroke={node.style.stroke} strokeWidth={Math.max(1.4, node.style.strokeWidth)} opacity={node.style.opacity} /></g>;
  }
  if (node.type === 'networkFirewall') {
    const rows = [18, 40, 62, 84];
    return <g><rect width={node.width} height={node.height} rx={Math.max(6, node.style.radius)} {...common} />{rows.map((y, row) => <g key={y}><line x1={12} x2={node.width - 12} y1={y} y2={y} stroke={node.style.stroke} strokeWidth={1} opacity={node.style.opacity * .75} />{[0,1,2,3].map((col) => { const offset = row % 2 ? 20 : 0; const x = 15 + offset + col * 42; return x < node.width - 12 ? <line key={col} x1={x} x2={x} y1={Math.max(0, y - 20)} y2={y} stroke={node.style.stroke} strokeWidth={1} opacity={node.style.opacity * .75} /> : null; })}</g>)}</g>;
  }
  if (node.type === 'networkClient') {
    return <g><rect x={16} y={10} width={node.width - 32} height={62} rx={6} {...common} /><line x1={node.width / 2} x2={node.width / 2} y1={72} y2={88} stroke={node.style.stroke} strokeWidth={Math.max(1.5, node.style.strokeWidth)} opacity={node.style.opacity} /><line x1={node.width * .32} x2={node.width * .68} y1={90} y2={90} stroke={node.style.stroke} strokeWidth={Math.max(1.5, node.style.strokeWidth)} opacity={node.style.opacity} /></g>;
  }
  if (node.type === 'networkLoadBalancer') {
    return <g><rect width={node.width} height={node.height} rx={Math.max(10, node.style.radius)} {...common} /><path d={`M 35 ${node.height/2} H ${node.width-35} M ${node.width/2} 22 V ${node.height-22} M ${node.width-46} ${node.height/2-10} L ${node.width-32} ${node.height/2} L ${node.width-46} ${node.height/2+10} M ${node.width/2-10} ${node.height-34} L ${node.width/2} ${node.height-20} L ${node.width/2+10} ${node.height-34}`} fill="none" stroke={node.style.stroke} strokeWidth={Math.max(1.4,node.style.strokeWidth)} opacity={node.style.opacity}/></g>;
  }
  if (node.type === 'networkDatabase') {
    const cap = Math.min(18, node.height * .2); return <g><path d={`M 0 ${cap} C 0 0, ${node.width} 0, ${node.width} ${cap} V ${node.height-cap} C ${node.width} ${node.height}, 0 ${node.height}, 0 ${node.height-cap} Z`} {...common}/><ellipse cx={node.width/2} cy={cap} rx={node.width/2} ry={cap} {...common}/></g>;
  }
  if (node.type === 'networkStorage') {
    return <g><rect width={node.width} height={node.height} rx={Math.max(8,node.style.radius)} {...common}/>{[22,46,70].map((y)=><rect key={y} x={18} y={y} width={node.width-36} height={14} rx={3} fill="none" stroke={node.style.stroke} strokeWidth={1.1} opacity={node.style.opacity}/>)}</g>;
  }
  if (['cloudAws','cloudAzure','cloudGcp','cloudService'].includes(node.type)) {
    const badge = node.type === 'cloudAws' ? 'AWS' : node.type === 'cloudAzure' ? 'AZ' : node.type === 'cloudGcp' ? 'GCP' : 'CLD';
    return <g><rect width={node.width} height={node.height} rx={Math.max(12,node.style.radius)} {...common}/><rect x={10} y={10} width={42} height={22} rx={7} fill={node.style.stroke} opacity={Math.min(.12,node.style.opacity)}/><text x={31} y={25} textAnchor="middle" fontSize={9} fontWeight={800} fill={node.style.stroke} opacity={node.style.opacity}>{badge}</text></g>;
  }
  if (node.type === 'networkCloud') {
    const w = node.width; const h = node.height;
    return <path d={`M ${w*.22} ${h*.78} C ${w*.08} ${h*.78}, ${w*.06} ${h*.55}, ${w*.2} ${h*.5} C ${w*.17} ${h*.27}, ${w*.39} ${h*.17}, ${w*.52} ${h*.31} C ${w*.62} ${h*.16}, ${w*.84} ${h*.25}, ${w*.82} ${h*.47} C ${w*.96} ${h*.5}, ${w*.94} ${h*.76}, ${w*.78} ${h*.78} Z`} {...common} />;
  }
  if (node.type === 'devCode') {
    return <g><rect width={node.width} height={node.height} rx={Math.max(10, node.style.radius)} {...common} /><rect x={0} y={0} width={node.width} height={28} rx={Math.max(10, node.style.radius)} fill={node.style.stroke} opacity={Math.min(.11, node.style.opacity)} pointerEvents="none" /><circle cx={16} cy={14} r={3.4} fill={node.style.stroke} opacity={node.style.opacity * .55} /><circle cx={28} cy={14} r={3.4} fill={node.style.stroke} opacity={node.style.opacity * .42} /><circle cx={40} cy={14} r={3.4} fill={node.style.stroke} opacity={node.style.opacity * .32} /><path d={`M ${node.width*.18} ${node.height*.56} L ${node.width*.28} ${node.height*.46} L ${node.width*.18} ${node.height*.36} M ${node.width*.82} ${node.height*.56} L ${node.width*.72} ${node.height*.46} L ${node.width*.82} ${node.height*.36} M ${node.width*.46} ${node.height*.3} L ${node.width*.38} ${node.height*.64}`} fill="none" stroke={node.style.stroke} strokeWidth={Math.max(1.7, node.style.strokeWidth)} strokeLinecap="round" strokeLinejoin="round" opacity={node.style.opacity} /></g>;
  }
  if (node.type === 'devApi') {
    const inset = Math.min(28, node.width * .14);
    return <g><path d={`M ${inset} 0 H ${node.width - inset} L ${node.width} ${node.height / 2} L ${node.width - inset} ${node.height} H ${inset} L 0 ${node.height / 2} Z`} {...common} /><text x={node.width / 2} y={Math.min(node.height - 14, 28)} textAnchor="middle" fontSize={18} fontWeight={800} fill={node.style.stroke} opacity={Math.min(.82, node.style.opacity)}>{'{ }'}</text></g>;
  }
  if (node.type === 'devMobile') {
    const inner = 9;
    return <g><rect width={node.width} height={node.height} rx={Math.max(18, node.style.radius)} {...common} /><rect x={inner} y={16} width={node.width - inner * 2} height={node.height - 38} rx={14} fill="none" stroke={node.style.stroke} strokeWidth={Math.max(1, node.style.strokeWidth * .8)} opacity={node.style.opacity} /><circle cx={node.width / 2} cy={node.height - 13} r={4.5} fill="none" stroke={node.style.stroke} strokeWidth={Math.max(1, node.style.strokeWidth * .8)} opacity={node.style.opacity} /></g>;
  }
  if (node.type === 'devBrowser') {
    return <g><rect width={node.width} height={node.height} rx={Math.max(9, node.style.radius)} {...common} /><rect x={0} y={0} width={node.width} height={24} rx={Math.max(9, node.style.radius)} fill={node.style.stroke} opacity={Math.min(.11, node.style.opacity)} pointerEvents="none" /><circle cx={15} cy={12} r={3.2} fill={node.style.stroke} opacity={node.style.opacity * .55} /><circle cx={27} cy={12} r={3.2} fill={node.style.stroke} opacity={node.style.opacity * .42} /><circle cx={39} cy={12} r={3.2} fill={node.style.stroke} opacity={node.style.opacity * .32} /><line x1={15} x2={node.width - 15} y1={42} y2={42} stroke={node.style.stroke} strokeWidth={Math.max(1, node.style.strokeWidth * .75)} opacity={node.style.opacity * .45} /><line x1={15} x2={node.width - 60} y1={57} y2={57} stroke={node.style.stroke} strokeWidth={Math.max(1, node.style.strokeWidth * .75)} opacity={node.style.opacity * .32} /></g>;
  }
  if (node.type === 'engineeringSensor') {
    const cx = node.width / 2; const cy = node.height / 2;
    return <g><circle cx={cx} cy={cy} r={Math.min(node.width, node.height) * .35} {...common} /><circle cx={cx} cy={cy} r={Math.min(node.width, node.height) * .08} fill={node.style.stroke} opacity={node.style.opacity} /><path d={`M ${cx} ${cy - 34} V ${cy - 18} M ${cx + 24} ${cy - 24} L ${cx + 13} ${cy - 13} M ${cx + 34} ${cy} H ${cx + 18} M ${cx + 24} ${cy + 24} L ${cx + 13} ${cy + 13} M ${cx} ${cy + 34} V ${cy + 18} M ${cx - 24} ${cy + 24} L ${cx - 13} ${cy + 13} M ${cx - 34} ${cy} H ${cx - 18} M ${cx - 24} ${cy - 24} L ${cx - 13} ${cy - 13}`} fill="none" stroke={node.style.stroke} strokeWidth={Math.max(1.3, node.style.strokeWidth)} strokeLinecap="round" opacity={node.style.opacity * .82} /></g>;
  }
  if (node.type === 'engineeringController') {
    return <g><rect width={node.width} height={node.height} rx={Math.max(10, node.style.radius)} {...common} />{Array.from({ length: 6 }, (_, index) => <line key={`p-l-${index}`} x1={0} x2={-9} y1={18 + index * 13} y2={18 + index * 13} stroke={node.style.stroke} strokeWidth={Math.max(1, node.style.strokeWidth * .8)} opacity={node.style.opacity} />)}{Array.from({ length: 6 }, (_, index) => <line key={`p-r-${index}`} x1={node.width} x2={node.width + 9} y1={18 + index * 13} y2={18 + index * 13} stroke={node.style.stroke} strokeWidth={Math.max(1, node.style.strokeWidth * .8)} opacity={node.style.opacity} />)}<rect x={26} y={22} width={node.width - 52} height={node.height - 44} rx={8} fill="none" stroke={node.style.stroke} strokeWidth={Math.max(1.1, node.style.strokeWidth * .8)} opacity={node.style.opacity} /></g>;
  }
  if (node.type === 'engineeringFormula') {
    return <g><rect width={node.width} height={node.height} rx={Math.max(10, node.style.radius)} {...common} /><path d={`M 20 25 H ${node.width - 20}`} fill="none" stroke={node.style.stroke} strokeWidth={Math.max(1.2, node.style.strokeWidth * .8)} opacity={node.style.opacity * .55} /><text x={34} y={Math.min(node.height - 18, 62)} fontSize={28} fontWeight={700} fill={node.style.stroke} opacity={Math.min(.8, node.style.opacity)}>∑</text></g>;
  }
  if (node.type === 'devTerminal') {
    return <g><rect width={node.width} height={node.height} rx={Math.max(9,node.style.radius)} {...common}/><rect x={0} y={0} width={node.width} height={25} rx={Math.max(9,node.style.radius)} fill={node.style.stroke} opacity={Math.min(.11,node.style.opacity)}/><path d={`M 22 48 L 34 58 L 22 68 M 43 70 H 70`} fill="none" stroke={node.style.stroke} strokeWidth={Math.max(1.8,node.style.strokeWidth)} strokeLinecap="round" strokeLinejoin="round" opacity={node.style.opacity}/></g>;
  }
  if (node.type === 'devContainer') {
    const w=node.width,h=node.height; return <g><path d={`M ${w*.18} ${h*.2} L ${w*.5} ${h*.06} L ${w*.82} ${h*.2} L ${w*.82} ${h*.78} L ${w*.5} ${h*.94} L ${w*.18} ${h*.78} Z`} {...common}/><path d={`M ${w*.18} ${h*.2} L ${w*.5} ${h*.36} L ${w*.82} ${h*.2} M ${w*.5} ${h*.36} V ${h*.94}`} fill="none" stroke={node.style.stroke} strokeWidth={Math.max(1.1,node.style.strokeWidth*.8)} opacity={node.style.opacity}/></g>;
  }
  if (node.type === 'devQueue') {
    return <g><rect width={node.width} height={node.height} rx={Math.max(10,node.style.radius)} {...common}/>{[28,52,76].map((y)=><g key={y}><circle cx={26} cy={y} r={6} fill="none" stroke={node.style.stroke} strokeWidth={Math.max(1,node.style.strokeWidth*.8)} opacity={node.style.opacity}/><line x1={42} x2={node.width-22} y1={y} y2={y} stroke={node.style.stroke} strokeWidth={Math.max(1,node.style.strokeWidth*.8)} opacity={node.style.opacity*.7}/></g>)}</g>;
  }
  if (node.type === 'devFunction') {
    const cut=Math.min(22,node.height*.24); return <g><path d={`M ${cut} 0 H ${node.width-cut} L ${node.width} ${node.height/2} L ${node.width-cut} ${node.height} H ${cut} L 0 ${node.height/2} Z`} {...common}/><text x={node.width/2} y={node.height/2+7} textAnchor="middle" fontSize={22} fontWeight={800} fill={node.style.stroke} opacity={Math.min(.6,node.style.opacity)}>ƒ</text></g>;
  }
  if (node.type === 'orgExecutive') {
    const band=Math.min(32,node.height*.3); return <g><rect width={node.width} height={node.height} rx={Math.max(12,node.style.radius)} {...common}/><rect width={node.width} height={band} rx={Math.max(12,node.style.radius)} fill={node.style.stroke} opacity={Math.min(.16,node.style.opacity)}/><circle cx={24} cy={band/2} r={7} fill="none" stroke={node.style.stroke} strokeWidth={Math.max(1,node.style.strokeWidth*.8)} opacity={node.style.opacity}/></g>;
  }
  if (node.type === 'orgTeam') {
    return <g><rect width={node.width} height={node.height} rx={Math.max(10,node.style.radius)} {...common}/><circle cx={32} cy={36} r={10} fill="none" stroke={node.style.stroke} strokeWidth={Math.max(1,node.style.strokeWidth*.8)} opacity={node.style.opacity}/><circle cx={52} cy={32} r={8} fill="none" stroke={node.style.stroke} strokeWidth={Math.max(1,node.style.strokeWidth*.8)} opacity={node.style.opacity*.8}/><path d={`M 17 61 C 20 49, 45 49, 48 61 M 41 57 C 44 48, 62 48, 65 57`} fill="none" stroke={node.style.stroke} strokeWidth={Math.max(1,node.style.strokeWidth*.8)} opacity={node.style.opacity}/></g>;
  }
  if (node.type === 'networkVpn') {
    return <g><rect width={node.width} height={node.height} rx={Math.max(node.height/2,node.style.radius)} {...common}/><path d={`M ${node.width*.38} ${node.height*.5} H ${node.width*.62} M ${node.width*.5} ${node.height*.32} V ${node.height*.68}`} stroke={node.style.stroke} strokeWidth={Math.max(1.6,node.style.strokeWidth)} opacity={node.style.opacity}/><path d={`M ${node.width*.3} ${node.height*.36} A ${node.height*.2} ${node.height*.2} 0 0 0 ${node.width*.3} ${node.height*.64} M ${node.width*.7} ${node.height*.36} A ${node.height*.2} ${node.height*.2} 0 0 1 ${node.width*.7} ${node.height*.64}`} fill="none" stroke={node.style.stroke} strokeWidth={Math.max(1.2,node.style.strokeWidth*.8)} opacity={node.style.opacity*.75}/></g>;
  }
  if (node.type === 'networkRack') {
    return <g><rect width={node.width} height={node.height} rx={Math.max(6,node.style.radius)} {...common}/>{Array.from({length:6},(_,i)=>{const y=18+i*26;return <g key={i}><rect x={14} y={y} width={node.width-28} height={18} rx={3} fill="none" stroke={node.style.stroke} strokeWidth={1} opacity={node.style.opacity*.8}/><circle cx={node.width-25} cy={y+9} r={2.3} fill={node.style.stroke} opacity={node.style.opacity*.7}/></g>})}</g>;
  }
  if (node.type === 'engineeringMotor') {
    const cx=node.width/2,cy=node.height/2; return <g><circle cx={cx} cy={cy} r={Math.min(node.height*.36,node.width*.3)} {...common}/><circle cx={cx} cy={cy} r={Math.min(node.height*.12,node.width*.09)} fill="none" stroke={node.style.stroke} strokeWidth={Math.max(1.2,node.style.strokeWidth)} opacity={node.style.opacity}/><line x1={cx+Math.min(node.height*.36,node.width*.3)} x2={node.width-5} y1={cy} y2={cy} stroke={node.style.stroke} strokeWidth={Math.max(1.8,node.style.strokeWidth)} opacity={node.style.opacity}/></g>;
  }
  if (node.type === 'engineeringBattery') {
    const cy=node.height/2; return <g><rect x={18} y={18} width={node.width-36} height={node.height-36} rx={Math.max(5,node.style.radius*.6)} {...common}/><line x1={node.width/2-16} x2={node.width/2-16} y1={cy-13} y2={cy+13} stroke={node.style.stroke} strokeWidth={Math.max(2,node.style.strokeWidth)} opacity={node.style.opacity}/><line x1={node.width/2-28} x2={node.width/2-4} y1={cy} y2={cy} stroke={node.style.stroke} strokeWidth={Math.max(2,node.style.strokeWidth)} opacity={node.style.opacity}/><line x1={node.width/2+19} x2={node.width/2+19} y1={cy-13} y2={cy+13} stroke={node.style.stroke} strokeWidth={Math.max(2,node.style.strokeWidth)} opacity={node.style.opacity}/></g>;
  }
  if (node.type === 'physicsVector') {
    const y=node.height/2; return <g><line x1={18} x2={node.width-28} y1={y} y2={y} stroke={node.style.stroke} strokeWidth={Math.max(2,node.style.strokeWidth)} opacity={node.style.opacity}/><path d={`M ${node.width-42} ${y-12} L ${node.width-24} ${y} L ${node.width-42} ${y+12}`} fill="none" stroke={node.style.stroke} strokeWidth={Math.max(2,node.style.strokeWidth)} strokeLinecap="round" strokeLinejoin="round" opacity={node.style.opacity}/></g>;
  }
  if (node.type === 'physicsWave') {
    const mid=node.height/2,amp=node.height*.24; const w=node.width; return <g><rect width={w} height={node.height} rx={Math.max(8,node.style.radius)} {...common}/><path d={`M 12 ${mid} C ${w*.12} ${mid-amp}, ${w*.2} ${mid-amp}, ${w*.28} ${mid} S ${w*.44} ${mid+amp}, ${w*.52} ${mid} S ${w*.68} ${mid-amp}, ${w*.76} ${mid} S ${w*.9} ${mid+amp}, ${w-12} ${mid}`} fill="none" stroke={node.style.stroke} strokeWidth={Math.max(1.8,node.style.strokeWidth)} opacity={node.style.opacity}/></g>;
  }
  return <rect width={node.width} height={node.height} rx={node.type === 'terminator' ? node.height / 2 : node.style.radius} {...common} />;
}

function endpointLabelPoint(points: Array<{ x: number; y: number }>, start: boolean): { x: number; y: number } | null {
  if (points.length < 2) return null;
  const a = start ? points[0] : points[points.length - 1];
  const b = start ? points[1] : points[points.length - 2];
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const length = Math.hypot(dx, dy) || 1;
  const along = 21;
  const side = 10;
  return { x: a.x + (dx / length) * along - (dy / length) * side, y: a.y + (dy / length) * along + (dx / length) * side };
}

function endpointMarker(marker: ConnectorEndpoint, atStart: boolean): string | undefined {
  if (marker === 'none') return undefined;
  if (marker === 'arrow') return atStart ? 'url(#arrowStart)' : 'url(#arrowHead)';
  if (marker === 'circle') return 'url(#endpointCircle)';
  if (marker === 'diamond') return 'url(#endpointDiamond)';
  if (marker === 'square') return 'url(#endpointSquare)';
  return undefined;
}

function EdgeView({ edge, source, target, allNodes, selected, editing, simplified = false, onSelect, onStartEdit, onEditChange, onFinishEdit, onContextMenu }: { edge: DiagramEdge; source: DiagramNode; target: DiagramNode; allNodes: DiagramNode[]; selected: boolean; editing: EdgeEditState | null; simplified?: boolean; onSelect: (event: React.PointerEvent<SVGPathElement>) => void; onStartEdit: (event: React.MouseEvent<SVGPathElement>) => void; onEditChange: (value: string) => void; onFinishEdit: (commit: boolean) => void; onContextMenu: (event: React.MouseEvent<SVGPathElement>) => void }) {
  const route = useMemo(() => routeEdge(edge, source, target, simplified ? [source, target] : allNodes), [edge, source, target, allNodes, simplified]);
  const relation = edge.relationshipType;
  const bpmnFlow = edge.bpmnFlowType;
  const dash = bpmnFlow === 'message' ? '8 6' : bpmnFlow === 'association' ? '3 5' : ['dependency', 'include', 'extend'].includes(String(relation)) ? '7 5' : edge.linePattern === 'dashed' ? '7 5' : undefined;
  const startEndpoint: ConnectorEndpoint = edge.startMarker ?? (edge.arrowStart ? 'arrow' : 'none');
  const endEndpoint: ConnectorEndpoint = edge.endMarker ?? (edge.arrowEnd ? 'arrow' : 'none');
  const relationMarkerStart = relation === 'composition' ? 'url(#diamondFilled)' : relation === 'aggregation' ? 'url(#diamondHollow)' : endpointMarker(startEndpoint, true);
  const relationMarkerEnd = bpmnFlow === 'association' ? undefined : relation === 'inheritance' ? 'url(#triangleHollow)' : ['dependency', 'include', 'extend'].includes(String(relation)) ? 'url(#arrowHead)' : endpointMarker(endEndpoint, false);
  const displayLabel = edge.label || (relation === 'include' ? '«include»' : relation === 'extend' ? '«extend»' : '');
  const sourceCardinalityPoint = edge.sourceCardinality ? endpointLabelPoint(route.points, true) : null;
  const targetCardinalityPoint = edge.targetCardinality ? endpointLabelPoint(route.points, false) : null;
  return (
    <g className={`edge-group ${selected ? 'selected' : ''}`}>
      <path d={route.path} fill="none" stroke="transparent" strokeWidth={16} className="edge-hit" onPointerDown={onSelect} onDoubleClick={onStartEdit} onContextMenu={onContextMenu} />
      <path
        d={route.path}
        fill="none"
        stroke={selected ? '#2563eb' : edge.stroke}
        strokeWidth={selected ? Math.max(2.4, edge.strokeWidth) : edge.strokeWidth}
        strokeDasharray={dash}
        markerStart={relationMarkerStart}
        markerEnd={relationMarkerEnd}
        className="diagram-edge"
        pointerEvents="none"
      />
      {editing?.id === edge.id ? (
        <foreignObject x={route.labelPoint.x - 60} y={route.labelPoint.y - 17} width={120} height={34}>
          <input
            autoFocus
            className="edge-label-editor"
            value={editing.label}
            onChange={(event) => onEditChange(event.target.value)}
            onPointerDown={(event) => event.stopPropagation()}
            onKeyDown={(event) => {
              if (event.key === 'Escape') { event.preventDefault(); onFinishEdit(false); }
              if (event.key === 'Enter') { event.preventDefault(); event.currentTarget.blur(); }
            }}
            onBlur={() => onFinishEdit(true)}
          />
        </foreignObject>
      ) : !simplified && displayLabel ? (
        <g transform={`translate(${route.labelPoint.x} ${route.labelPoint.y})`} pointerEvents="none">
          <rect x={-48} y={-15} width={96} height={30} rx={8} className="edge-label-bg" />
          <text textAnchor="middle" dominantBaseline="middle" className="edge-label">{displayLabel}</text>
        </g>
      ) : null}
      {!simplified && sourceCardinalityPoint && <text x={sourceCardinalityPoint.x} y={sourceCardinalityPoint.y} className="edge-cardinality" textAnchor="middle" dominantBaseline="middle" pointerEvents="none">{edge.sourceCardinality}</text>}
      {!simplified && targetCardinalityPoint && <text x={targetCardinalityPoint.x} y={targetCardinalityPoint.y} className="edge-cardinality" textAnchor="middle" dominantBaseline="middle" pointerEvents="none">{edge.targetCardinality}</text>}
    </g>
  );
}

export function DiagramCanvas(props: Props) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [resize, setResize] = useState<ResizeState | null>(null);
  const [rotate, setRotate] = useState<RotateState | null>(null);
  const [groupResize, setGroupResize] = useState<GroupResizeState | null>(null);
  const [waypointDrag, setWaypointDrag] = useState<WaypointDragState | null>(null);
  const [pan, setPan] = useState<PanState | null>(null);
  const [marquee, setMarquee] = useState<MarqueeState | null>(null);
  const [editing, setEditing] = useState<EditState | null>(null);
  const [editingEdge, setEditingEdge] = useState<EdgeEditState | null>(null);
  const [guides, setGuides] = useState<{ x?: number; y?: number }>({});
  const [equalSpacingGuides, setEqualSpacingGuides] = useState<EqualSpacingGuide[]>([]);
  const [distanceGuides, setDistanceGuides] = useState<DistanceGuide[]>([]);
  const [rotationGuide, setRotationGuide] = useState<{ x: number; y: number; rotation: number; snapped: boolean } | null>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [zoomInteracting, setZoomInteracting] = useState(false);
  const zoomInteractionTimer = useRef<number | null>(null);
  const nodeById = useMemo(() => new Map(props.page.nodes.map((node) => [node.id, node])), [props.page.nodes]);
  const visibleNodes = useMemo(() => props.page.nodes.filter((node) => !node.hidden), [props.page.nodes]);
  const spatialNodeIndex = useMemo(() => buildSpatialNodeIndex(props.page.nodes), [props.page.nodes]);
  const spatialEdgeIndex = useMemo(() => buildSpatialEdgeIndex(props.page.nodes, props.page.edges), [props.page.nodes, props.page.edges]);
  useEffect(() => {
    const element = svgRef.current;
    if (!element) return;
    const update = () => {
      const rect = element.getBoundingClientRect();
      setCanvasSize((current) => current.width === rect.width && current.height === rect.height ? current : { width: rect.width, height: rect.height });
    };
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  useEffect(() => () => { if (zoomInteractionTimer.current !== null) window.clearTimeout(zoomInteractionTimer.current); }, []);
  const interactionActive = Boolean(drag || resize || rotate || groupResize || waypointDrag || pan || zoomInteracting);
  const simplifiedInteraction = Boolean(props.adaptiveQuality && interactionActive && (props.page.nodes.length > Math.max(80, Math.floor((props.renderThreshold ?? 300) * 0.55)) || props.page.edges.length > 120));

  const renderSubset = useMemo(() => computeRenderSubset({
    nodes: props.page.nodes,
    edges: props.page.edges,
    viewport: props.viewport,
    width: canvasSize.width,
    height: canvasSize.height,
    selectedNodeIds: props.selectedNodeIds,
    selectedEdgeIds: props.selectedEdgeIds,
    threshold: props.renderThreshold,
    marginPx: props.cullingMarginPx,
    nodeIndex: spatialNodeIndex,
    edgeIndex: spatialEdgeIndex,
  }), [props.page.nodes, props.page.edges, props.viewport, canvasSize, props.selectedNodeIds, props.selectedEdgeIds, props.renderThreshold, props.cullingMarginPx, spatialNodeIndex, spatialEdgeIndex]);
  const renderedNodes = useMemo(() => visibleNodes.filter((node) => renderSubset.nodeIds.has(node.id)), [visibleNodes, renderSubset]);
  const renderedEdges = useMemo(() => props.page.edges.filter((edge) => renderSubset.edgeIds.has(edge.id)), [props.page.edges, renderSubset]);
  const selectedTransformNodes = useMemo(() => visibleNodes.filter((node) => props.selectedNodeIds.includes(node.id) && !node.locked), [visibleNodes, props.selectedNodeIds]);
  const selectionBounds = useMemo(() => {
    if (selectedTransformNodes.length < 2) return null;
    const left = Math.min(...selectedTransformNodes.map((node) => node.x));
    const top = Math.min(...selectedTransformNodes.map((node) => node.y));
    const right = Math.max(...selectedTransformNodes.map((node) => node.x + node.width));
    const bottom = Math.max(...selectedTransformNodes.map((node) => node.y + node.height));
    return { left, top, right, bottom, width: right - left, height: bottom - top };
  }, [selectedTransformNodes]);
  const miniMapBounds = useMemo(() => {
    if (visibleNodes.length === 0) return null;
    const bounds = visibleNodes.map((node) => nodeBounds(node));
    const left = Math.min(...bounds.map((bound) => bound.left)) - 60;
    const top = Math.min(...bounds.map((bound) => bound.top)) - 60;
    const right = Math.max(...bounds.map((bound) => bound.right)) + 60;
    const bottom = Math.max(...bounds.map((bound) => bound.bottom)) + 60;
    return { left, top, width: Math.max(1, right - left), height: Math.max(1, bottom - top) };
  }, [visibleNodes]);

  function worldFromClient(clientX: number, clientY: number): Point {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return {
      x: (clientX - rect.left - props.viewport.panX) / props.viewport.zoom,
      y: (clientY - rect.top - props.viewport.panY) / props.viewport.zoom,
    };
  }

  function spacingGuidesFor(node: DiagramNode, x: number, y: number, excluded: Set<string>): EqualSpacingGuide[] {
    const tolerance = 7 / Math.max(.2, props.viewport.zoom);
    const cx = x + node.width / 2;
    const cy = y + node.height / 2;
    const candidates = visibleNodes.filter((other) => !excluded.has(other.id));
    const guides: EqualSpacingGuide[] = [];

    const horizontalPeers = candidates.filter((other) => Math.abs((other.y + other.height / 2) - cy) <= Math.max(36, Math.max(node.height, other.height) * .7));
    const left = horizontalPeers.filter((other) => other.x + other.width <= x + tolerance).sort((a,b) => (b.x + b.width) - (a.x + a.width))[0];
    const right = horizontalPeers.filter((other) => other.x >= x + node.width - tolerance).sort((a,b) => a.x - b.x)[0];
    if (left && right) {
      const gapA = x - (left.x + left.width);
      const gapB = right.x - (x + node.width);
      if (gapA >= 0 && gapB >= 0 && Math.abs(gapA - gapB) <= tolerance) {
        guides.push({ axis:'horizontal', gap:(gapA + gapB) / 2, firstStart:left.x + left.width, firstEnd:x, secondStart:x + node.width, secondEnd:right.x, cross:Math.min(y, left.y, right.y) - 18 });
      }
    }

    const verticalPeers = candidates.filter((other) => Math.abs((other.x + other.width / 2) - cx) <= Math.max(36, Math.max(node.width, other.width) * .7));
    const above = verticalPeers.filter((other) => other.y + other.height <= y + tolerance).sort((a,b) => (b.y + b.height) - (a.y + a.height))[0];
    const below = verticalPeers.filter((other) => other.y >= y + node.height - tolerance).sort((a,b) => a.y - b.y)[0];
    if (above && below) {
      const gapA = y - (above.y + above.height);
      const gapB = below.y - (y + node.height);
      if (gapA >= 0 && gapB >= 0 && Math.abs(gapA - gapB) <= tolerance) {
        guides.push({ axis:'vertical', gap:(gapA + gapB) / 2, firstStart:above.y + above.height, firstEnd:y, secondStart:y + node.height, secondEnd:below.y, cross:Math.min(x, above.x, below.x) - 18 });
      }
    }
    return guides;
  }

  function distanceGuidesFor(node: DiagramNode, x: number, y: number, excluded: Set<string>): DistanceGuide[] {
    const result: DistanceGuide[] = [];
    const right = x + node.width;
    const bottom = y + node.height;
    const cx = x + node.width / 2;
    const cy = y + node.height / 2;
    for (const other of visibleNodes) {
      if (excluded.has(other.id) || other.hidden) continue;
      const otherRight = other.x + other.width;
      const otherBottom = other.y + other.height;
      const verticalOverlap = Math.min(bottom, otherBottom) - Math.max(y, other.y);
      const horizontalOverlap = Math.min(right, otherRight) - Math.max(x, other.x);
      const centerYDelta = Math.abs((other.y + other.height / 2) - cy);
      const centerXDelta = Math.abs((other.x + other.width / 2) - cx);
      if (verticalOverlap >= -24 || centerYDelta <= Math.max(node.height, other.height)) {
        if (other.x >= right) result.push({ axis:'horizontal', from:right, to:other.x, cross:(cy + other.y + other.height / 2) / 2, gap:other.x-right });
        else if (otherRight <= x) result.push({ axis:'horizontal', from:otherRight, to:x, cross:(cy + other.y + other.height / 2) / 2, gap:x-otherRight });
      }
      if (horizontalOverlap >= -24 || centerXDelta <= Math.max(node.width, other.width)) {
        if (other.y >= bottom) result.push({ axis:'vertical', from:bottom, to:other.y, cross:(cx + other.x + other.width / 2) / 2, gap:other.y-bottom });
        else if (otherBottom <= y) result.push({ axis:'vertical', from:otherBottom, to:y, cross:(cx + other.x + other.width / 2) / 2, gap:y-otherBottom });
      }
    }
    return result.filter((guide)=>guide.gap >= 0 && guide.gap <= 1200).sort((a,b)=>a.gap-b.gap);
  }

  function startNodeDrag(event: React.PointerEvent<SVGGElement>, node: DiagramNode) {
    event.stopPropagation();
    if (editing?.id === node.id) return;
    if (props.readOnly) { props.onSelectNode(node.id, event.shiftKey); return; }
    const remoteEditor = (props.remoteUsers ?? []).find((user) => user.pageId === props.page.id && user.nodeIds.includes(node.id) && ['owner','edit'].includes(user.permission ?? 'view'));
    if (remoteEditor) { props.onSelectNode(node.id, event.shiftKey); return; }
    if (props.connectionMode) {
      props.onNodeForConnection(node.id, 'auto', event.shiftKey);
      return;
    }
    if (node.locked) {
      props.onSelectNode(node.id, event.shiftKey);
      return;
    }

    const alreadySelected = props.selectedNodeIds.includes(node.id);
    let ids = alreadySelected && props.selectedNodeIds.length > 1 ? props.selectedNodeIds : [node.id];
    if (!alreadySelected && (node.type === 'bpmnLane' || node.type === 'bpmnPool')) {
      const containsCenter = (candidate: DiagramNode) => {
        const cx = candidate.x + candidate.width / 2;
        const cy = candidate.y + candidate.height / 2;
        return cx >= node.x && cx <= node.x + node.width && cy >= node.y && cy <= node.y + node.height;
      };
      const allowed = node.type === 'bpmnPool'
        ? new Set(['bpmnLane','bpmnTask','bpmnSubprocess','bpmnDataObject','bpmnEvent','bpmnGateway'])
        : new Set(['bpmnTask','bpmnSubprocess','bpmnDataObject','bpmnEvent','bpmnGateway']);
      ids = [node.id, ...visibleNodes.filter((candidate) => candidate.id !== node.id && allowed.has(candidate.type) && containsCenter(candidate)).map((candidate) => candidate.id)];
    }
    if (!alreadySelected) props.onSelectNode(node.id, event.shiftKey);
    const starts = ids
      .map((id) => nodeById.get(id))
      .filter((value): value is DiagramNode => value !== undefined && !value.locked)
      .map((item) => ({ id: item.id, x: item.x, y: item.y }));
    if (starts.length === 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    setDrag({ pointerId: event.pointerId, startClientX: event.clientX, startClientY: event.clientY, starts });
  }

  function moveNode(event: React.PointerEvent<SVGGElement>) {
    if (!drag || drag.pointerId !== event.pointerId) return;
    const dx = (event.clientX - drag.startClientX) / props.viewport.zoom;
    const dy = (event.clientY - drag.startClientY) / props.viewport.zoom;
    const primaryStart = drag.starts[0];
    const primary = primaryStart ? nodeById.get(primaryStart.id) : null;
    let guideOffsetX = 0;
    let guideOffsetY = 0;
    let guideX: number | undefined;
    let guideY: number | undefined;
    if (primary) {
      const rawX = primaryStart.x + dx;
      const rawY = primaryStart.y + dy;
      const movingX = [rawX, rawX + primary.width / 2, rawX + primary.width];
      const movingY = [rawY, rawY + primary.height / 2, rawY + primary.height];
      const selectedIds = new Set(drag.starts.map((item) => item.id));
      let bestX = 7 / props.viewport.zoom;
      let bestY = 7 / props.viewport.zoom;
      for (const other of visibleNodes) {
        if (selectedIds.has(other.id)) continue;
        const otherX = [other.x, other.x + other.width / 2, other.x + other.width];
        const otherY = [other.y, other.y + other.height / 2, other.y + other.height];
        for (const mx of movingX) for (const ox of otherX) {
          const difference = ox - mx;
          if (Math.abs(difference) < Math.abs(bestX)) { bestX = difference; guideOffsetX = difference; guideX = ox; }
        }
        for (const my of movingY) for (const oy of otherY) {
          const difference = oy - my;
          if (Math.abs(difference) < Math.abs(bestY)) { bestY = difference; guideOffsetY = difference; guideY = oy; }
        }
      }
    }
    setGuides({ x: guideX, y: guideY });
    if (primary) {
      const excluded = new Set(drag.starts.map((item) => item.id));
      const candidateX = primaryStart.x + dx + guideOffsetX;
      const candidateY = primaryStart.y + dy + guideOffsetY;
      setEqualSpacingGuides(spacingGuidesFor(primary, candidateX, candidateY, excluded));
      setDistanceGuides(distanceGuidesFor(primary, candidateX, candidateY, excluded));
    } else setEqualSpacingGuides([]);
    props.onMoveNodes(drag.starts.map((item) => ({
      id: item.id,
      x: guideX === undefined && props.preferences.snapToGrid ? Math.round((item.x + dx) / props.preferences.gridSize) * props.preferences.gridSize : item.x + dx + guideOffsetX,
      y: guideY === undefined && props.preferences.snapToGrid ? Math.round((item.y + dy) / props.preferences.gridSize) * props.preferences.gridSize : item.y + dy + guideOffsetY,
    })), false);
  }

  function endNodeDrag(event: React.PointerEvent<SVGGElement>) {
    if (!drag || drag.pointerId !== event.pointerId) return;
    const finalPositions = drag.starts.map((item) => {
      const current = nodeById.get(item.id);
      return { id: item.id, x: current?.x ?? item.x, y: current?.y ?? item.y };
    });
    props.onMoveNodes(finalPositions, true);
    setDrag(null);
    setGuides({});
    setEqualSpacingGuides([]);
    setDistanceGuides([]);
  }

  function startResize(event: React.PointerEvent<SVGRectElement>, node: DiagramNode) {
    event.stopPropagation();
    if (props.readOnly) return;
    if (node.locked) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    setResize({ id: node.id, pointerId: event.pointerId, startClientX: event.clientX, startClientY: event.clientY, startWidth: node.width, startHeight: node.height });
  }

  function moveResize(event: React.PointerEvent<SVGRectElement>) {
    if (!resize || resize.pointerId !== event.pointerId) return;
    const dw = (event.clientX - resize.startClientX) / props.viewport.zoom;
    const dh = (event.clientY - resize.startClientY) / props.viewport.zoom;
    const snap = props.preferences.snapToGrid ? props.preferences.gridSize : 1;
    props.onResizeNode(resize.id, Math.max(50, Math.round((resize.startWidth + dw) / snap) * snap), Math.max(40, Math.round((resize.startHeight + dh) / snap) * snap), false);
  }

  function endResize(event: React.PointerEvent<SVGRectElement>) {
    if (!resize || resize.pointerId !== event.pointerId) return;
    const node = nodeById.get(resize.id);
    if (node) props.onResizeNode(resize.id, node.width, node.height, true);
    setResize(null);
  }

  function startGroupResize(event: React.PointerEvent<SVGRectElement>, handle: GroupResizeState['handle']) {
    if (props.readOnly) return;
    if (!selectionBounds || selectedTransformNodes.length < 2) return;
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    setGroupResize({
      pointerId: event.pointerId,
      handle,
      startClientX: event.clientX,
      startClientY: event.clientY,
      bounds: { left: selectionBounds.left, top: selectionBounds.top, right: selectionBounds.right, bottom: selectionBounds.bottom },
      starts: selectedTransformNodes.map((node) => ({ id: node.id, x: node.x, y: node.y, width: node.width, height: node.height })),
    });
  }

  function moveGroupResize(event: React.PointerEvent<SVGRectElement>) {
    if (!groupResize || groupResize.pointerId !== event.pointerId) return;
    const dx = (event.clientX - groupResize.startClientX) / props.viewport.zoom;
    const dy = (event.clientY - groupResize.startClientY) / props.viewport.zoom;
    const start = groupResize.bounds;
    let left = start.left;
    let right = start.right;
    let top = start.top;
    let bottom = start.bottom;
    if (groupResize.handle.includes('w')) left = Math.min(start.right - 60, start.left + dx);
    if (groupResize.handle.includes('e')) right = Math.max(start.left + 60, start.right + dx);
    if (groupResize.handle.includes('n')) top = Math.min(start.bottom - 60, start.top + dy);
    if (groupResize.handle.includes('s')) bottom = Math.max(start.top + 60, start.bottom + dy);
    const startWidth = Math.max(1, start.right - start.left);
    const startHeight = Math.max(1, start.bottom - start.top);
    let width = Math.max(60, right - left);
    let height = Math.max(60, bottom - top);
    if (event.shiftKey) {
      const ratio = startWidth / startHeight;
      if (width / height > ratio) width = height * ratio;
      else height = width / ratio;
      if (groupResize.handle.includes('w')) left = right - width; else right = left + width;
      if (groupResize.handle.includes('n')) top = bottom - height; else bottom = top + height;
    }
    const scaleX = width / startWidth;
    const scaleY = height / startHeight;
    props.onTransformNodes(groupResize.starts.map((item) => ({
      id: item.id,
      x: left + (item.x - start.left) * scaleX,
      y: top + (item.y - start.top) * scaleY,
      width: item.width * scaleX,
      height: item.height * scaleY,
    })), false);
  }

  function endGroupResize(event: React.PointerEvent<SVGRectElement>) {
    if (!groupResize || groupResize.pointerId !== event.pointerId) return;
    const final = groupResize.starts.map((item) => {
      const node = nodeById.get(item.id);
      return { id: item.id, x: node?.x ?? item.x, y: node?.y ?? item.y, width: node?.width ?? item.width, height: node?.height ?? item.height };
    });
    props.onTransformNodes(final, true);
    setGroupResize(null);
  }

  function startWaypointDrag(event: React.PointerEvent<SVGCircleElement>, edgeId: string, index: number) {
    event.stopPropagation();
    if (props.readOnly) return;
    const edge = props.page.edges.find((item) => item.id === edgeId);
    if (!edge) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    setWaypointDrag({ edgeId, index, pointerId: event.pointerId, working: [...(edge.waypoints ?? [])] });
  }

  function startSegmentWaypointDrag(event: React.PointerEvent<SVGCircleElement>, edge: DiagramEdge, routePoints: Point[], segmentIndex: number) {
    event.stopPropagation();
    if (props.readOnly) return;
    const point = worldFromClient(event.clientX, event.clientY);
    const snap = props.preferences.snapToGrid ? props.preferences.gridSize : 1;
    const snapped = { x: Math.round(point.x / snap) * snap, y: Math.round(point.y / snap) * snap };
    const working = edge.routingMode === 'manual' && edge.waypoints?.length ? [...edge.waypoints] : routePoints.slice(1, -1).map((item) => ({ ...item }));
    const index = Math.max(0, Math.min(segmentIndex, working.length));
    working.splice(index, 0, snapped);
    props.onUpdateEdgeWaypoints(edge.id, working, false);
    event.currentTarget.setPointerCapture(event.pointerId);
    setWaypointDrag({ edgeId: edge.id, index, pointerId: event.pointerId, working });
  }

  function moveWaypoint(event: React.PointerEvent<SVGCircleElement>) {
    if (!waypointDrag || waypointDrag.pointerId !== event.pointerId) return;
    const point = worldFromClient(event.clientX, event.clientY);
    const snap = props.preferences.snapToGrid ? props.preferences.gridSize : 1;
    const next = [...waypointDrag.working];
    next[waypointDrag.index] = { x: Math.round(point.x / snap) * snap, y: Math.round(point.y / snap) * snap };
    setWaypointDrag((current) => current ? { ...current, working: next } : current);
    props.onUpdateEdgeWaypoints(waypointDrag.edgeId, next, false);
  }

  function endWaypointDrag(event: React.PointerEvent<SVGCircleElement>) {
    if (!waypointDrag || waypointDrag.pointerId !== event.pointerId) return;
    const point = worldFromClient(event.clientX, event.clientY);
    const snap = props.preferences.snapToGrid ? props.preferences.gridSize : 1;
    const finalWaypoints = [...waypointDrag.working];
    finalWaypoints[waypointDrag.index] = { x: Math.round(point.x / snap) * snap, y: Math.round(point.y / snap) * snap };
    props.onUpdateEdgeWaypoints(waypointDrag.edgeId, finalWaypoints, false);
    props.onUpdateEdgeWaypoints(waypointDrag.edgeId, finalWaypoints, true);
    setWaypointDrag(null);
  }

  function startRotate(event: React.PointerEvent<SVGCircleElement>, node: DiagramNode) {
    event.stopPropagation();
    if (props.readOnly) return;
    if (node.locked) return;
    const pointer = worldFromClient(event.clientX, event.clientY);
    const center = { x: node.x + node.width / 2, y: node.y + node.height / 2 };
    const pointerAngle = Math.atan2(pointer.y - center.y, pointer.x - center.x) * 180 / Math.PI + 90;
    event.currentTarget.setPointerCapture(event.pointerId);
    setRotate({ id: node.id, pointerId: event.pointerId, offset: node.rotation - pointerAngle });
  }

  function moveRotate(event: React.PointerEvent<SVGCircleElement>) {
    if (!rotate || rotate.pointerId !== event.pointerId) return;
    const node = nodeById.get(rotate.id);
    if (!node) return;
    const pointer = worldFromClient(event.clientX, event.clientY);
    const center = { x: node.x + node.width / 2, y: node.y + node.height / 2 };
    let rotation = Math.atan2(pointer.y - center.y, pointer.x - center.x) * 180 / Math.PI + 90 + rotate.offset;
    rotation = ((rotation + 180) % 360 + 360) % 360 - 180;
    if (event.shiftKey) rotation = Math.round(rotation / 15) * 15;
    const axes = [-180, -90, 0, 90, 180];
    const nearest = axes.reduce((best, axis) => Math.abs(rotation - axis) < Math.abs(rotation - best) ? axis : best, axes[0]);
    const snapped = Math.abs(rotation - nearest) <= 6;
    if (snapped) rotation = nearest === 180 ? -180 : nearest;
    setRotationGuide({ x: center.x, y: center.y, rotation, snapped });
    props.onRotateNode(rotate.id, rotation, false);
  }

  function endRotate(event: React.PointerEvent<SVGCircleElement>) {
    if (!rotate || rotate.pointerId !== event.pointerId) return;
    const node = nodeById.get(rotate.id);
    if (node) props.onRotateNode(rotate.id, node.rotation, true);
    setRotate(null);
    setRotationGuide(null);
  }

  function startBackground(event: React.PointerEvent<SVGSVGElement>) {
    if (event.target !== event.currentTarget && (event.target as SVGElement).dataset.background !== 'true') return;
    setEditing(null);
    setEditingEdge(null);
    const shouldPan = event.button === 1 || event.altKey;
    event.currentTarget.setPointerCapture(event.pointerId);
    if (shouldPan) {
      setPan({ pointerId: event.pointerId, startClientX: event.clientX, startClientY: event.clientY, startPanX: props.viewport.panX, startPanY: props.viewport.panY });
      return;
    }
    const start = worldFromClient(event.clientX, event.clientY);
    setMarquee({ pointerId: event.pointerId, start, end: start, additive: event.shiftKey });
  }

  function moveBackground(event: React.PointerEvent<SVGSVGElement>) {
    props.onCursor?.(worldFromClient(event.clientX, event.clientY));
    const currentPan = pan;
    if (currentPan && currentPan.pointerId === event.pointerId) {
      props.onViewport({ ...props.viewport, panX: currentPan.startPanX + event.clientX - currentPan.startClientX, panY: currentPan.startPanY + event.clientY - currentPan.startClientY });
      return;
    }
    const currentMarquee = marquee;
    if (currentMarquee?.pointerId === event.pointerId) {
      setMarquee((current) => current ? { ...current, end: worldFromClient(event.clientX, event.clientY) } : current);
    }
  }

  function endBackground(event: React.PointerEvent<SVGSVGElement>) {
    const currentPan = pan;
    if (currentPan && currentPan.pointerId === event.pointerId) {
      setPan(null);
      return;
    }
    const currentMarquee = marquee;
    if (!currentMarquee || currentMarquee.pointerId !== event.pointerId) return;
    const end = worldFromClient(event.clientX, event.clientY);
    const left = Math.min(currentMarquee.start.x, end.x);
    const right = Math.max(currentMarquee.start.x, end.x);
    const top = Math.min(currentMarquee.start.y, end.y);
    const bottom = Math.max(currentMarquee.start.y, end.y);
    const moved = Math.abs(end.x - currentMarquee.start.x) + Math.abs(end.y - currentMarquee.start.y) > 4;
    if (!moved) {
      props.onSelectNode(null);
      props.onSelectEdge(null);
    } else {
      const ids = visibleNodes
        .filter((node) => node.x < right && node.x + node.width > left && node.y < bottom && node.y + node.height > top)
        .map((node) => node.id);
      props.onSelectArea(ids, currentMarquee.additive);
    }
    setMarquee(null);
  }

  function handleWheel(event: React.WheelEvent<SVGSVGElement>) {
    event.preventDefault();
    if (props.adaptiveQuality) {
      setZoomInteracting(true);
      if (zoomInteractionTimer.current !== null) window.clearTimeout(zoomInteractionTimer.current);
      zoomInteractionTimer.current = window.setTimeout(() => { setZoomInteracting(false); zoomInteractionTimer.current = null; }, 140);
    }
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const previousZoom = props.viewport.zoom;
    const factor = event.deltaY < 0 ? 1.1 : 0.9;
    const zoom = Math.min(3, Math.max(0.2, previousZoom * factor));
    const pointerX = event.clientX - rect.left;
    const pointerY = event.clientY - rect.top;
    const worldX = (pointerX - props.viewport.panX) / previousZoom;
    const worldY = (pointerY - props.viewport.panY) / previousZoom;
    props.onViewport({ zoom, panX: pointerX - worldX * zoom, panY: pointerY - worldY * zoom });
  }

  function finishEditing(commit = true) {
    if (!editing) return;
    const nextText = editing.text.trim() || 'Sem texto';
    if (commit) props.onEditNodeText(editing.id, nextText);
    setEditing(null);
  }

  function finishEdgeEditing(commit = true) {
    if (!editingEdge) return;
    if (commit) props.onEditEdgeLabel(editingEdge.id, editingEdge.label.trim());
    setEditingEdge(null);
  }

  function centerFromMiniMap(event: React.PointerEvent<SVGSVGElement>) {
    if (!miniMapBounds) return;
    event.stopPropagation();
    const mapRect = event.currentTarget.getBoundingClientRect();
    const canvasRect = svgRef.current?.getBoundingClientRect();
    if (!canvasRect || mapRect.width <= 0 || mapRect.height <= 0) return;
    const ratioX = Math.min(1, Math.max(0, (event.clientX - mapRect.left) / mapRect.width));
    const ratioY = Math.min(1, Math.max(0, (event.clientY - mapRect.top) / mapRect.height));
    const worldX = miniMapBounds.left + miniMapBounds.width * ratioX;
    const worldY = miniMapBounds.top + miniMapBounds.height * ratioY;
    props.onViewport({ ...props.viewport, panX: canvasRect.width / 2 - worldX * props.viewport.zoom, panY: canvasRect.height / 2 - worldY * props.viewport.zoom });
  }

  function handleDrop(event: React.DragEvent<SVGSVGElement>) {
    event.preventDefault();
    if (props.readOnly) return;
    const type = event.dataTransfer.getData('application/x-owdex-shape');
    if (!type) return;
    const point = worldFromClient(event.clientX, event.clientY);
    props.onDropShape(type, point.x, point.y);
  }

  const marqueeRect = marquee ? {
    x: Math.min(marquee.start.x, marquee.end.x),
    y: Math.min(marquee.start.y, marquee.end.y),
    width: Math.abs(marquee.end.x - marquee.start.x),
    height: Math.abs(marquee.end.y - marquee.start.y),
  } : null;

  return (
    <main id="diagram-editor" tabIndex={-1} className={`canvas-shell ${(props.connectionMode || props.connectionSourceId) ? 'connection-mode' : ''} ${simplifiedInteraction ? 'adaptive-low-detail' : ''}`} aria-label="Editor de diagrama">
      {(props.connectionMode || props.connectionSourceId) && <div className="connection-hint">{props.connectionSourceId ? 'Escolha o destino · segure Shift para ligar vários destinos sem sair do modo' : 'Selecione o objeto de origem'}</div>}
      <div className="canvas-tip">Arraste para selecionar • Alt + arrastar para mover a tela • Duplo clique edita texto • Alt + duplo clique na linha adiciona ponto</div>
      <svg
        ref={svgRef}
        className="diagram-canvas"
        onPointerDown={startBackground}
        onPointerMove={moveBackground}
        onPointerUp={endBackground}
        onPointerCancel={(event) => { endBackground(event); props.onCursor?.(null); }}
        onPointerLeave={() => props.onCursor?.(null)}
        onWheel={handleWheel}
        onDragOver={(event) => { if (event.dataTransfer.types.includes('application/x-owdex-shape')) { event.preventDefault(); event.dataTransfer.dropEffect = 'copy'; } }}
        onDrop={handleDrop}
        onContextMenu={(event) => {
          event.preventDefault();
          const target = event.target as SVGElement;
          if (event.target === event.currentTarget || target.dataset.background === 'true') {
            props.onSelectNode(null); props.onSelectEdge(null);
            props.onContextMenuRequest({ x: event.clientX, y: event.clientY, target: 'canvas' });
          }
        }}
        aria-label="Área de edição do diagrama"
      >
        <defs>
          <pattern id="smallGrid" width={props.preferences.gridSize * 2} height={props.preferences.gridSize * 2} patternUnits="userSpaceOnUse">
            <path d={`M ${props.preferences.gridSize * 2} 0 L 0 0 0 ${props.preferences.gridSize * 2}`} fill="none" stroke="#dfe3e8" strokeWidth="0.7" />
          </pattern>
          <marker id="arrowHead" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="context-stroke" />
          </marker>
          <marker id="arrowStart" viewBox="0 0 10 10" refX="1" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M 10 0 L 0 5 L 10 10 z" fill="context-stroke" />
          </marker>
          <marker id="endpointCircle" viewBox="0 0 12 12" refX="6" refY="6" markerWidth="8" markerHeight="8" orient="auto">
            <circle cx="6" cy="6" r="3.6" fill="#fff" stroke="context-stroke" strokeWidth="1.6" />
          </marker>
          <marker id="endpointDiamond" viewBox="0 0 14 14" refX="7" refY="7" markerWidth="9" markerHeight="9" orient="auto">
            <path d="M 7 1 L 13 7 L 7 13 L 1 7 Z" fill="#fff" stroke="context-stroke" strokeWidth="1.5" />
          </marker>
          <marker id="endpointSquare" viewBox="0 0 12 12" refX="6" refY="6" markerWidth="8" markerHeight="8" orient="auto">
            <rect x="2" y="2" width="8" height="8" rx="1" fill="#fff" stroke="context-stroke" strokeWidth="1.5" />
          </marker>
          <marker id="triangleHollow" viewBox="0 0 12 12" refX="11" refY="6" markerWidth="9" markerHeight="9" orient="auto-start-reverse">
            <path d="M 1 1 L 11 6 L 1 11 Z" fill="#fff" stroke="context-stroke" strokeWidth="1.2" />
          </marker>
          <marker id="diamondHollow" viewBox="0 0 14 14" refX="1" refY="7" markerWidth="10" markerHeight="10" orient="auto-start-reverse">
            <path d="M 1 7 L 7 1 L 13 7 L 7 13 Z" fill="#fff" stroke="context-stroke" strokeWidth="1.2" />
          </marker>
          <marker id="diamondFilled" viewBox="0 0 14 14" refX="1" refY="7" markerWidth="10" markerHeight="10" orient="auto-start-reverse">
            <path d="M 1 7 L 7 1 L 13 7 L 7 13 Z" fill="context-stroke" stroke="context-stroke" strokeWidth="1.2" />
          </marker>
        </defs>
        <rect data-background="true" width="100%" height="100%" fill="#f6f7f9" />
        <g transform={`translate(${props.viewport.panX} ${props.viewport.panY}) scale(${props.viewport.zoom})`}>
          <rect data-background="true" x={-6000} y={-6000} width={18000} height={18000} fill={props.preferences.gridVisible ? 'url(#smallGrid)' : 'transparent'} />

          {renderedEdges.map((edge) => {
            const source = nodeById.get(edge.sourceId);
            const target = nodeById.get(edge.targetId);
            return source && target && !source.hidden && !target.hidden ? (
              <EdgeView
                key={edge.id}
                edge={edge}
                source={source}
                target={target}
                allNodes={visibleNodes}
                selected={props.selectedEdgeIds.includes(edge.id)}
                editing={editingEdge}
                simplified={simplifiedInteraction}
                onSelect={(event) => {
                  event.stopPropagation();
                  props.onSelectEdge(edge.id, event.shiftKey);
                }}
                onStartEdit={(event) => {
                  event.stopPropagation();
                  props.onSelectEdge(edge.id);
                  if (!props.readOnly && event.altKey) {
                    const point = worldFromClient(event.clientX, event.clientY);
                    const next = [...(edge.waypoints ?? []), point];
                    props.onUpdateEdgeWaypoints(edge.id, next, false);
                    props.onUpdateEdgeWaypoints(edge.id, next, true);
                    return;
                  }
                  if (!props.readOnly) setEditingEdge({ id: edge.id, label: edge.label });
                }}
                onEditChange={(value) => setEditingEdge((current) => current?.id === edge.id ? { ...current, label: value } : current)}
                onFinishEdit={finishEdgeEditing}
                onContextMenu={(event) => {
                  event.preventDefault(); event.stopPropagation();
                  if (!props.selectedEdgeIds.includes(edge.id)) props.onSelectEdge(edge.id);
                  props.onContextMenuRequest({ x: event.clientX, y: event.clientY, target: 'edge' });
                }}
              />
            ) : null;
          })}

          {props.selectedEdgeIds.length === 1 && (() => {
            const edge = props.page.edges.find((item) => item.id === props.selectedEdgeIds[0]);
            if (!edge) return null;
            const source = nodeById.get(edge.sourceId);
            const target = nodeById.get(edge.targetId);
            if (!source || !target) return null;
            const routed = routeEdge(edge, source, target, visibleNodes);
            return <g className="edge-edit-tools">
              {routed.points.slice(0, -1).map((point, segmentIndex) => {
                const next = routed.points[segmentIndex + 1];
                const mid = { x:(point.x + next.x) / 2, y:(point.y + next.y) / 2 };
                return <circle
                  key={`segment-${edge.id}-${segmentIndex}`}
                  className="edge-segment-handle"
                  cx={mid.x}
                  cy={mid.y}
                  r={7}
                  aria-label="Arrastar segmento da conexão"
                  onPointerDown={(event) => startSegmentWaypointDrag(event, edge, routed.points, segmentIndex)}
                  onPointerMove={moveWaypoint}
                  onPointerUp={endWaypointDrag}
                  onPointerCancel={endWaypointDrag}
                />;
              })}
              {edge.routingMode === 'manual' && (edge.waypoints ?? []).map((point, index) => (
                <circle
                  key={`waypoint-${edge.id}-${index}`}
                  className="waypoint-handle"
                  cx={point.x}
                  cy={point.y}
                  r={8}
                  onPointerDown={(event) => startWaypointDrag(event, edge.id, index)}
                  onPointerMove={moveWaypoint}
                  onPointerUp={endWaypointDrag}
                  onPointerCancel={endWaypointDrag}
                  onDoubleClick={(event) => {
                    event.stopPropagation();
                    const next = (edge.waypoints ?? []).filter((_, pointIndex) => pointIndex !== index);
                    props.onUpdateEdgeWaypoints(edge.id, next, false);
                    props.onUpdateEdgeWaypoints(edge.id, next, true);
                  }}
                />
              ))}
            </g>;
          })()}

          {renderedNodes.map((node) => {
            const selected = props.selectedNodeIds.includes(node.id);
            const connectionSource = props.connectionSourceId === node.id;
            const isEditing = editing?.id === node.id;
            return (
              <g
                key={node.id}
                transform={`translate(${node.x} ${node.y}) rotate(${node.rotation} ${node.width / 2} ${node.height / 2})`}
                className={`diagram-node ${selected ? 'selected' : ''} ${connectionSource ? 'connection-source' : ''} ${node.locked ? 'locked' : ''}`}
                tabIndex={0}
                role="button"
                aria-label={`${node.text || node.type}${node.locked ? ', travado' : ''}${typeof node.data.linkPageId === 'string' && node.data.linkPageId ? ', possui link para outra página' : ''}`}
                onFocus={() => props.onSelectNode(node.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && typeof node.data.linkPageId === 'string' && node.data.linkPageId) { event.preventDefault(); props.onNavigateNodeLink(node.data.linkPageId); }
                }}
                onPointerDown={(event) => startNodeDrag(event, node)}
                onPointerMove={moveNode}
                onPointerUp={endNodeDrag}
                onPointerCancel={endNodeDrag}
                onContextMenu={(event) => {
                  event.preventDefault(); event.stopPropagation();
                  if (!props.selectedNodeIds.includes(node.id)) props.onSelectNode(node.id);
                  props.onContextMenuRequest({ x: event.clientX, y: event.clientY, target: 'node' });
                }}
                onDoubleClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (!props.readOnly && !props.connectionMode && !node.locked) {
                    setDrag(null);
                    setResize(null);
                    setGroupResize(null);
                    props.onSelectNode(node.id);
                    setEditing({ id: node.id, text: node.text });
                  }
                }}
              >
                <NodeShape node={node} />
                {typeof node.data.linkPageId === 'string' && node.data.linkPageId && !isEditing && (
                  <g
                    className="node-page-link"
                    role="button"
                    tabIndex={0}
                    aria-label="Abrir página vinculada"
                    transform={`translate(${Math.max(14, node.width - 14)} 14)`}
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={(event) => { event.stopPropagation(); props.onNavigateNodeLink(String(node.data.linkPageId)); }}
                    onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); event.stopPropagation(); props.onNavigateNodeLink(String(node.data.linkPageId)); } }}
                  >
                    <circle cx={0} cy={0} r={10} />
                    <text x={0} y={0} textAnchor="middle" dominantBaseline="central">↗</text>
                  </g>
                )}
                {isEditing ? (
                  <foreignObject x={8} y={7} width={Math.max(30, node.width - 16)} height={Math.max(30, node.height - 14)}>
                    <textarea
                      autoFocus
                      className="node-editor"
                      value={editing.text}
                      onChange={(event) => setEditing({ ...editing, text: event.target.value })}
                      onPointerDown={(event) => event.stopPropagation()}
                      onKeyDown={(event) => {
                        if (event.key === 'Escape') { event.preventDefault(); finishEditing(false); }
                        else if (event.key === 'Enter' && (event.ctrlKey || event.metaKey || !event.shiftKey)) {
                          event.preventDefault();
                          const nextText = event.currentTarget.value.trim() || 'Sem texto';
                          props.onEditNodeText(node.id, nextText);
                          setEditing(null);
                        }
                      }}
                      onBlur={(event) => {
                        const nextText = event.currentTarget.value.trim() || 'Sem texto';
                        props.onEditNodeText(node.id, nextText);
                        setEditing(null);
                      }}
                    />
                  </foreignObject>
                ) : (
                  <foreignObject x={10} y={8} width={Math.max(20, node.width - 20)} height={Math.max(20, node.height - 16)} pointerEvents="none">
                    {node.type === 'orgPerson' ? (
                      <div className="org-card-text" style={{ color: node.style.textColor, opacity: node.style.opacity }}>
                        <strong>{String(node.data.name ?? node.text)}</strong>
                        <span>{String(node.data.role ?? node.data.cargo ?? '')}</span>
                        {String(node.data.department ?? '').trim() && <small>{String(node.data.department)}</small>}
                      </div>
                    ) : node.type === 'orgDepartment' ? (
                      <div className="org-department-text" style={{ color: node.style.textColor, opacity: node.style.opacity }}>
                        <strong>{String(node.data.department ?? node.text)}</strong>
                        {node.text !== String(node.data.department ?? node.text) && <span>{node.text}</span>}
                      </div>
                    ) : node.type === 'erdEntity' ? (
                      <div className="erd-card-text" style={{ color: node.style.textColor, opacity: node.style.opacity }}>
                        <strong>{node.text}</strong>
                        <div className="erd-field-list">
                          {(node.entityFields ?? []).slice(0, 10).map((field) => <span key={field.id}><b>{field.primaryKey ? 'PK' : field.foreignKey ? 'FK' : ''}</b><i>{field.name}</i><em>{field.dataType}</em></span>)}
                          {(node.entityFields?.length ?? 0) > 10 && <small>+{(node.entityFields?.length ?? 0) - 10} campo(s)</small>}
                        </div>
                      </div>
                    ) : node.type === 'umlClass' ? (
                      <div className="uml-class-text" style={{ color: node.style.textColor, opacity: node.style.opacity }}>
                        <strong>{node.text}</strong>
                        <div>{(node.umlAttributes ?? []).slice(0, 8).map((item, index) => <span key={`a-${index}`}>{item}</span>)}</div>
                        <div>{(node.umlMethods ?? []).slice(0, 8).map((item, index) => <span key={`m-${index}`}>{item}</span>)}</div>
                      </div>
                    ) : node.type === 'umlActor' ? (
                      <div className="uml-actor-text" style={{ color: node.style.textColor, opacity: node.style.opacity }}><strong>{node.text}</strong></div>
                    ) : ['networkServer', 'networkSwitch', 'networkRouter', 'networkFirewall', 'networkClient', 'networkCloud', 'networkLoadBalancer', 'networkDatabase', 'networkStorage'].includes(node.type) ? (
                      <div className="network-card-text" style={{ color: node.style.textColor, opacity: node.style.opacity }}>
                        <strong>{String(node.data.hostname || node.text)}</strong>
                        {String(node.data.ip ?? '').trim() && <span>IP {String(node.data.ip)}</span>}
                        {String(node.data.vlan ?? '').trim() && <small>VLAN {String(node.data.vlan)}</small>}
                      </div>
                    ) : ['cloudAws','cloudAzure','cloudGcp','cloudService'].includes(node.type) ? (
                      <div className="network-card-text cloud-card-text" style={{ color: node.style.textColor, opacity: node.style.opacity }}>
                        <strong>{node.text}</strong>
                        {String(node.data.service ?? '').trim() && <span>{String(node.data.service)}</span>}
                        <small>{[node.data.provider,node.data.region,node.data.environment].filter(Boolean).join(' · ')}</small>
                      </div>
                    ) : (
                      <div className="node-text" style={{ color: node.style.textColor, fontSize: node.style.fontSize, textAlign: node.style.textAlign, fontWeight: node.style.fontWeight, justifyContent: node.style.textAlign === 'left' ? 'flex-start' : node.style.textAlign === 'right' ? 'flex-end' : 'center', opacity: node.style.opacity }}>{node.text}</div>
                    )}
                  </foreignObject>
                )}
                {selected && props.selectedNodeIds.length === 1 && !props.connectionMode && !props.connectionSourceId && !isEditing && (
                  <>
                    <rect className={`selection-outline ${node.locked ? 'locked' : ''}`} x={-5} y={-5} width={node.width + 10} height={node.height + 10} rx={6} />
                    {props.selectedNodeIds.length === 1 && !node.locked && (
                      <rect
                        className="resize-handle"
                        x={node.width - 5}
                        y={node.height - 5}
                        width={12}
                        height={12}
                        rx={3}
                        onPointerDown={(event) => startResize(event, node)}
                        onPointerMove={moveResize}
                        onPointerUp={endResize}
                        onPointerCancel={endResize}
                      />
                    )}
                    {props.selectedNodeIds.length === 1 && !node.locked && (
                      <>
                        <line className="rotation-stem" x1={node.width / 2} y1={-5} x2={node.width / 2} y2={-30} />
                        <circle
                          className="rotation-handle"
                          cx={node.width / 2}
                          cy={-36}
                          r={7}
                          onPointerDown={(event) => startRotate(event, node)}
                          onPointerMove={moveRotate}
                          onPointerUp={endRotate}
                          onPointerCancel={endRotate}
                        />
                      </>
                    )}
                    {props.selectedNodeIds.length === 1 && !node.locked && ANCHORS.map((side) => {
                      const point = localAnchorPoint(node.width, node.height, side);
                      return <circle key={side} className="anchor-dot quick-connect-anchor" cx={point.x} cy={point.y} r={5} tabIndex={0} role="button" aria-label={`Conectar pelo lado ${side}`} onPointerDown={(event) => { event.stopPropagation(); props.onNodeForConnection(node.id, side, event.shiftKey); }} />;
                    })}
                  </>
                )}
                {(props.connectionMode || props.connectionSourceId) && ANCHORS.map((side) => {
                  const point = localAnchorPoint(node.width, node.height, side);
                  const active = props.connectionSourceId === node.id && props.connectionSourceAnchor === side;
                  return (
                    <circle
                      key={`connect-${side}`}
                      className={`connection-anchor ${active ? 'active' : ''}`}
                      cx={point.x}
                      cy={point.y}
                      r={6}
                      onPointerDown={(event) => { event.stopPropagation(); props.onNodeForConnection(node.id, side, event.shiftKey); }}
                    />
                  );
                })}
                {(props.commentCounts?.[node.id] ?? 0) > 0 && !isEditing && <g className="node-comment-badge" role="button" tabIndex={0} transform={`translate(${node.width - 8} ${node.height - 8})`} onPointerDown={(event) => event.stopPropagation()} onClick={(event) => { event.stopPropagation(); props.onOpenNodeComments?.(node.id); }} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); props.onOpenNodeComments?.(node.id); } }}><circle cx={0} cy={0} r={12} /><text x={0} y={1} textAnchor="middle" dominantBaseline="central">{Math.min(99, props.commentCounts?.[node.id] ?? 0)}</text></g>}
                {node.locked && selected && <text x={node.width - 7} y={14} textAnchor="end" className="lock-indicator">🔒</text>}
                {(() => { const remoteEditor = (props.remoteUsers ?? []).find((user) => user.pageId === props.page.id && user.nodeIds.includes(node.id) && ['owner','edit'].includes(user.permission ?? 'view')); return remoteEditor ? <g className="remote-edit-lock" pointerEvents="none" transform="translate(8 -28)"><rect x={0} y={0} width={Math.min(190, Math.max(88, remoteEditor.email.length * 7 + 25))} height={22} rx={6} /><text x={8} y={15}>Em uso · {remoteEditor.email}</text></g> : null; })()}
              </g>
            );
          })}

          {selectionBounds && !props.connectionMode && !props.connectionSourceId && (
            <g className="group-transform">
              <rect className="group-selection-outline" x={selectionBounds.left - 7} y={selectionBounds.top - 7} width={selectionBounds.width + 14} height={selectionBounds.height + 14} rx={5} />
              {([
                ['nw', selectionBounds.left - 12, selectionBounds.top - 12],
                ['ne', selectionBounds.right, selectionBounds.top - 12],
                ['sw', selectionBounds.left - 12, selectionBounds.bottom],
                ['se', selectionBounds.right, selectionBounds.bottom],
              ] as const).map(([handle, x, y]) => (
                <rect
                  key={handle}
                  className={`group-resize-handle ${handle}`}
                  x={x}
                  y={y}
                  width={12}
                  height={12}
                  rx={3}
                  onPointerDown={(event) => startGroupResize(event, handle)}
                  onPointerMove={moveGroupResize}
                  onPointerUp={endGroupResize}
                  onPointerCancel={endGroupResize}
                />
              ))}
              <text className="group-selection-label" x={selectionBounds.left} y={selectionBounds.top - 17}>{selectedTransformNodes.length} objetos</text>
            </g>
          )}


          {(props.remoteUsers ?? []).filter((user) => user.pageId === props.page.id).map((user) => {
            const color = `hsl(${(user.userId.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0) * 47) % 360} 72% 46%)`;
            const selectedRemoteNodes = user.nodeIds.map((id) => nodeById.get(id)).filter((node): node is DiagramNode => Boolean(node && !node.hidden));
            return <g key={`remote-${user.userId}`} className="remote-presence-layer" pointerEvents="none">
              {selectedRemoteNodes.map((node) => { const bounds = nodeBounds(node); return <rect key={`${user.userId}-${node.id}`} x={bounds.left - 5} y={bounds.top - 5} width={bounds.right - bounds.left + 10} height={bounds.bottom - bounds.top + 10} rx={7} fill="none" stroke={color} strokeWidth={2 / Math.max(.35, props.viewport.zoom)} strokeDasharray={`${6 / Math.max(.35, props.viewport.zoom)} ${4 / Math.max(.35, props.viewport.zoom)}`} opacity={.82} />; })}
              {user.cursor && <g transform={`translate(${user.cursor.x} ${user.cursor.y})`}>
                <path d="M 0 0 L 0 19 L 5 14 L 9 24 L 13 22 L 9 12 L 17 12 Z" fill={color} stroke="#fff" strokeWidth={1.5 / Math.max(.35, props.viewport.zoom)} />
                <g transform="translate(15 17)"><rect x={0} y={0} width={Math.min(190, Math.max(72, user.email.length * 7.2 + 18))} height={24} rx={6} fill={color} /><text x={9} y={16} fill="#fff" fontSize={12} fontWeight={700}>{user.email}</text></g>
              </g>}
            </g>;
          })}

          {distanceGuides.map((guide, index) => guide.axis === 'horizontal' ? (
            <g key={`distance-h-${index}`} className="distance-guide" pointerEvents="none">
              <line x1={guide.from} x2={guide.to} y1={guide.cross} y2={guide.cross} />
              <line x1={guide.from} x2={guide.from} y1={guide.cross-4} y2={guide.cross+4} />
              <line x1={guide.to} x2={guide.to} y1={guide.cross-4} y2={guide.cross+4} />
              <g transform={`translate(${(guide.from+guide.to)/2} ${guide.cross-8})`}><rect x={-24} y={-13} width={48} height={19} rx={6}/><text textAnchor="middle" y={1}>{Math.round(guide.gap)} px</text></g>
            </g>
          ) : (
            <g key={`distance-v-${index}`} className="distance-guide" pointerEvents="none">
              <line x1={guide.cross} x2={guide.cross} y1={guide.from} y2={guide.to} />
              <line x1={guide.cross-4} x2={guide.cross+4} y1={guide.from} y2={guide.from} />
              <line x1={guide.cross-4} x2={guide.cross+4} y1={guide.to} y2={guide.to} />
              <g transform={`translate(${guide.cross+30} ${(guide.from+guide.to)/2})`}><rect x={-24} y={-10} width={48} height={20} rx={6}/><text textAnchor="middle" y={4}>{Math.round(guide.gap)} px</text></g>
            </g>
          ))}

          {equalSpacingGuides.map((guide, index) => guide.axis === 'horizontal' ? (
            <g key={`spacing-h-${index}`} className="equal-spacing-guide" pointerEvents="none">
              <line x1={guide.firstStart} x2={guide.firstEnd} y1={guide.cross} y2={guide.cross} />
              <line x1={guide.secondStart} x2={guide.secondEnd} y1={guide.cross} y2={guide.cross} />
              {[guide.firstStart,guide.firstEnd,guide.secondStart,guide.secondEnd].map((x,tick)=><line key={tick} x1={x} x2={x} y1={guide.cross-5} y2={guide.cross+5} />)}
              <g transform={`translate(${(guide.firstStart + guide.firstEnd) / 2} ${guide.cross - 9})`}><rect x={-25} y={-14} width={50} height={20} rx={6}/><text textAnchor="middle" y={0}>{Math.round(guide.gap)} px =</text></g>
              <g transform={`translate(${(guide.secondStart + guide.secondEnd) / 2} ${guide.cross - 9})`}><rect x={-25} y={-14} width={50} height={20} rx={6}/><text textAnchor="middle" y={0}>{Math.round(guide.gap)} px</text></g>
            </g>
          ) : (
            <g key={`spacing-v-${index}`} className="equal-spacing-guide" pointerEvents="none">
              <line x1={guide.cross} x2={guide.cross} y1={guide.firstStart} y2={guide.firstEnd} />
              <line x1={guide.cross} x2={guide.cross} y1={guide.secondStart} y2={guide.secondEnd} />
              {[guide.firstStart,guide.firstEnd,guide.secondStart,guide.secondEnd].map((y,tick)=><line key={tick} x1={guide.cross-5} x2={guide.cross+5} y1={y} y2={y} />)}
              <g transform={`translate(${guide.cross - 10} ${(guide.firstStart + guide.firstEnd) / 2})`}><rect x={-48} y={-10} width={45} height={20} rx={6}/><text textAnchor="middle" x={-25} y={4}>{Math.round(guide.gap)} px =</text></g>
              <g transform={`translate(${guide.cross - 10} ${(guide.secondStart + guide.secondEnd) / 2})`}><rect x={-42} y={-10} width={39} height={20} rx={6}/><text textAnchor="middle" x={-22} y={4}>{Math.round(guide.gap)} px</text></g>
            </g>
          ))}

          {rotationGuide && <g className={`rotation-axis-guides ${rotationGuide.snapped ? 'snapped' : ''}`} pointerEvents="none">
            <line x1={-6000} x2={12000} y1={rotationGuide.y} y2={rotationGuide.y} />
            <line x1={rotationGuide.x} x2={rotationGuide.x} y1={-6000} y2={12000} />
            <g transform={`translate(${rotationGuide.x + 18} ${rotationGuide.y - 18})`}><rect x={-4} y={-15} width={64} height={23} rx={7} /><text x={4} y={1}>{Math.round(rotationGuide.rotation)}°{rotationGuide.snapped ? ' • eixo' : ''}</text></g>
          </g>}
          {guides.x !== undefined && <line className="smart-guide" x1={guides.x} x2={guides.x} y1={-6000} y2={12000} />}
          {guides.y !== undefined && <line className="smart-guide" x1={-6000} x2={12000} y1={guides.y} y2={guides.y} />}
          {marqueeRect && <rect className="selection-marquee" {...marqueeRect} />}
        </g>
      </svg>
      {miniMapBounds && visibleNodes.length >= 2 && (
        <div className="minimap" title="Minimapa — clique para centralizar">
          <svg
            viewBox={`${miniMapBounds.left} ${miniMapBounds.top} ${miniMapBounds.width} ${miniMapBounds.height}`}
            preserveAspectRatio="none"
            onPointerDown={centerFromMiniMap}
            aria-label="Minimapa do diagrama"
          >
            <rect x={miniMapBounds.left} y={miniMapBounds.top} width={miniMapBounds.width} height={miniMapBounds.height} className="minimap-bg" />
            {visibleNodes.map((node) => {
              const bound = nodeBounds(node);
              return <rect key={node.id} x={bound.left} y={bound.top} width={Math.max(2, bound.right - bound.left)} height={Math.max(2, bound.bottom - bound.top)} className={`minimap-node ${props.selectedNodeIds.includes(node.id) ? 'selected' : ''}`} />;
            })}
          </svg>
        </div>
      )}
    </main>
  );
}
